import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SalesDetailPage from './SalesDetailPage';
import { PROMO_CODE_COLUMNS, SORT_TYPES, getDefaultVisibleColumns, getDefaultHiddenColumns } from '../constants/promoCodesColumnsConfig';
import { buildPromoCodesFilterGroups } from '../constants/promoCodesFiltersConfig';
import { compactCurrency } from '../utils/reportingUtils';

const STATUS_BADGES = {
  active: { bg: 'var(--sl-bg-success-primary, #ecfdf3)', color: 'var(--sl-text-success, #027a48)' },
  expired: { bg: 'var(--sl-bg-tertiary, #f2f4f7)', color: 'var(--sl-text-secondary, #344054)' },
  upcoming: { bg: 'var(--sl-bg-brand-primary, #eff8ff)', color: 'var(--sl-fg-brand, #175cd3)' },
};

const TYPE_BADGES = {
  Confirm: { bg: 'var(--sl-bg-brand-primary, #eff8ff)', color: 'var(--sl-fg-brand, #175cd3)' },
  Universal: { bg: 'var(--sl-bg-warning-primary, #fffaeb)', color: 'var(--sl-text-warning, #b54708)' },
  Membership: { bg: '#f4f3ff', color: '#5925dc' },
};

const buildDetailApiFilters = (activeFilters) => {
  const params = {};
  if (activeFilters.type && activeFilters.type.length > 0) {
    params.types = activeFilters.type;
  }
  if (activeFilters.status && activeFilters.status.length > 0) {
    params.statuses = activeFilters.status;
  }
  if (activeFilters.event && activeFilters.event.length > 0) {
    params.event_ids = activeFilters.event;
  }
  if (activeFilters.class && activeFilters.class.length > 0) {
    params.class_template_ids = activeFilters.class;
  }
  if (activeFilters.venue && activeFilters.venue.length > 0) {
    params.venue_ids = activeFilters.venue;
  }
  return params;
};

const PROMO_CODES_CONFIG = {
  pageTitle: 'Promo Codes',
  pageSubtitle: 'Promo code performance across your venues.',
  sidebarKey: 'marketing_promo_codes',
  defaultPreset: 'year_to_date',
  dateModeOptions: [
    { key: 'order_date', label: 'Transaction Date' },
    { key: 'event_date', label: 'Event Date' },
  ],
  defaultSortRules: [{ field: 'created_at', direction: 'desc' }],

  columns: PROMO_CODE_COLUMNS,
  sortTypes: SORT_TYPES,
  getDefaultVisibleColumns,
  getDefaultHiddenColumns,
  localStorageKey: (teamId) => `reportingSalesPromoCodeColumns_${teamId}`,
  firstColumnId: 'name',

  kpiFromDetailResponse: true,
  salesDataKey: null,
  venueSeriesKey: null,
  detailEndpoint: (teamId) => `/teams/${teamId}/reporting/marketing/promo-codes/data.json`,
  detailResponseKey: 'promo_codes',
  exportBasePath: (teamId) => `/teams/${teamId}/reporting/marketing/promo-codes/export`,
  detailErrorMessage: 'Failed to load promo code data.',
  detailTableTitle: 'Promo Codes',
  emptyMessage: 'No promo codes found.',
  searchPlaceholder: 'Search promo codes...',
  paginationLabel: 'promo codes',
  itemLabel: (count) => `promo code${count !== 1 ? 's' : ''}`,

  buildDetailApiFilters,

  buildKpiCards: (current, prior) => [
    { label: 'Total Promo Codes', value: (current.total_codes || 0).toLocaleString(), current: current.total_codes || 0, prior: prior.total_codes || 0 },
    { label: 'Total Redemptions', value: (current.total_redemptions || 0).toLocaleString(), current: current.total_redemptions || 0, prior: prior.total_redemptions || 0 },
    { label: 'Total Discount Given', value: compactCurrency(current.total_discount_given || 0), current: current.total_discount_given || 0, prior: prior.total_discount_given || 0 },
    { label: 'Avg Discount', value: compactCurrency(current.avg_discount_per_redemption || 0), current: current.avg_discount_per_redemption || 0, prior: prior.avg_discount_per_redemption || 0 },
  ],
  kpiGridColumns: 4,

  overTimeKey: null,
  topCharts: [],
  groupByOptions: null,
  groupByDefault: null,

  renderCellValue: (col, row, { formatEventDateTime, currencyFormatter: cf }) => {
    switch (col.id) {
      case 'name': return row.name || '\u2014';
      case 'promo_type': {
        const badge = TYPE_BADGES[row.promo_type];
        if (badge) return <span className="badge" style={{ background: badge.bg, color: badge.color, fontSize: 12 }}>{row.promo_type}</span>;
        return row.promo_type || '\u2014';
      }
      case 'discount_display': return row.discount_display || '\u2014';
      case 'event_name': return row.event_name || '\u2014';
      case 'venue_name': return row.venue_name || '\u2014';
      case 'usage': {
        const count = row.usage_count != null ? row.usage_count : 0;
        const limit = row.unlimited ? '\u221E' : (row.limit != null ? row.limit : '\u221E');
        return `${count} / ${limit}`;
      }
      case 'total_discount': return cf.format(row.total_discount || 0);
      case 'revenue_generated': return cf.format(row.revenue_generated || 0);
      case 'created_at': return <span className="text-nowrap">{formatEventDateTime(row.created_at)}</span>;
      case 'status': {
        const badge = STATUS_BADGES[row.status];
        if (badge) return <span className="badge" style={{ background: badge.bg, color: badge.color, fontSize: 12 }}>{row.status ? row.status.charAt(0).toUpperCase() + row.status.slice(1) : '\u2014'}</span>;
        return row.status || '\u2014';
      }
      default: return '\u2014';
    }
  },

  filterTitle: 'Filter Promo Codes',
  filterSubtitle: 'Apply filters to narrow promo code data.',
};

function ReportingMarketingPromoCodesPage({ team, csrfToken, ...props }) {
  const [filterOptions, setFilterOptions] = useState({});

  useEffect(() => {
    if (!team?.id) return;
    if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    axios.get(`/teams/${team.id}/reporting/marketing/promo-codes/filter_options.json`)
      .then((res) => setFilterOptions(res.data || {}))
      .catch(() => {});
  }, [team?.id, csrfToken]);

  const configWithFilters = {
    ...PROMO_CODES_CONFIG,
    buildFilterGroups: () => buildPromoCodesFilterGroups(filterOptions),
  };
  return <SalesDetailPage {...props} team={team} csrfToken={csrfToken} config={configWithFilters} />;
}

export default ReportingMarketingPromoCodesPage;
